import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import type {LegalPageCookiePolicyQuery} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import RichText from '@helios/shared/components/rich-text'
import {Section, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

interface Props {
    content: LegalPageCookiePolicyQuery['allPrismicLegalPageCookiePolicy']['nodes'][number]['data']
}

const LegalCookiePolicyContent = ({content}: Props) => {
    if (!content) {
        return handleNoPrismicData()
    }
    return (
        <>
            {content.sections.map((cookiePolicySection, i) => {
                return (
                    <Section key={`${cookiePolicySection.section_title}-${i}`}>
                        <FeatureBlock
                            title={cookiePolicySection.section_title}
                            image={cookiePolicySection.section_image}
                            mirrored={cookiePolicySection.inverse}
                        >
                            <RichText content={cookiePolicySection.section_content.richText} />
                        </FeatureBlock>
                    </Section>
                )
            })}

            <SpacedSection>
                <Layout variant="two-asymmetrical">
                    <RichText variant="title-base" content={content.used_cookies_title} />
                </Layout>
                <div>
                    {content.used_cookies_details.map((usedCookie, i) => {
                        return (
                            <UsedCookieRow key={`${usedCookie.used_cookie_title}-${i}`}>
                                <Text variant="paragraph-base">{usedCookie.used_cookie_title}</Text>
                                <RichText
                                    variant="paragraph-base"
                                    content={usedCookie.used_cookie_detail.richText}
                                />
                            </UsedCookieRow>
                        )
                    })}
                </div>
            </SpacedSection>

            {hasNonEmptyItems(content.sections_part_2) &&
                content.sections_part_2.map((cookiePolicySection, i) => {
                    return (
                        <Section key={`${cookiePolicySection.section_title}-${i}`}>
                            <FeatureBlock
                                title={cookiePolicySection.section_title}
                                image={cookiePolicySection.section_image}
                                mirrored={cookiePolicySection.inverse}
                            >
                                <RichText content={cookiePolicySection.section_content.richText} />
                            </FeatureBlock>
                        </Section>
                    )
                })}
        </>
    )
}

const SpacedSection = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing40};
`

const UsedCookieRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    padding-top: ${tokens.spacing10};
    padding-bottom: ${tokens.spacing10};
    border-bottom: 1px solid ${tokens.shade300};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 4fr;
    }

    &:last-child {
        border-bottom: none;
    }
`

export default LegalCookiePolicyContent
